// import external libraries
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// import services
import { mutations } from 'RestAPIs/rapini';
import showSnackbar, { showSuccessSnackbar } from 'components/shared/Snackbar';

// import types
import type {
  EditAppointmentTypeResponse,
  EventResponse,
  SchedulingActionResultResponse
} from 'generated/index';
import type { OutsideTreatmentAction } from './OutsideTreatmentAppointmentCard.types';

/**
 * The delete and unassign actions on an outside-treatment card, wired to the endpoints afsprakenplanning
 * uses: `deleteAppointment` for the delete, and `bulkEditParticipants` for the unassign — removing only
 * this care receiver from the appointment through `removeParticipants`, the same surgical endpoint the
 * appointments-overview EditParticipants drawer uses. (Re-saving the whole appointment via
 * `updateAppointment` would clear *every* care receiver on a shared appointment, since it rebuilds the
 * participant list from a partial event; the remove-participant action touches nobody else.)
 * A one-off appointment is changed straight away; a recurring one first asks whether to change only this
 * occurrence or this and all following ones (surfaced through `pendingAction` + `confirmRecurrence`),
 * passed through as the `editAppointmentType`. On success the backlog is refreshed so the card drops out.
 */
export const useOutsideTreatmentAppointmentActions = (
  event: EventResponse,
  careReceiverId: string
) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] =
    useState<OutsideTreatmentAction | null>(null);

  // The same feedback afsprakenplanning gives: refresh + a success toast, or the generic error toast.
  const onSuccess = () => {
    queryClient.invalidateQueries('backlog');
    showSuccessSnackbar();
  };
  const onError = () => {
    showSnackbar({ severity: 'error', message: t('somethingWentWrong') });
  };

  // Bulk-edit-participants returns an HTTP 200 with `success: false` (and ERROR notifications) as its
  // normal way of reporting a rejected edit — the mapper sets `.success(notifications.isEmpty())` — so
  // `onError` is never called for it. Check the result before reporting success, the same way the
  // appointments-overview EditParticipants hook does, or a no-op edit would still show "gelukt".
  const onUnassignSettled = (data?: SchedulingActionResultResponse) => {
    if (data?.success === false) {
      onError();
      return;
    }
    onSuccess();
  };

  const { mutate: deleteAppointment } = mutations.useDeleteAppointment({
    onSuccess,
    onError
  });
  const { mutate: bulkEditParticipants } =
    mutations.useAppointmentBulkEditParticipantsAction({
      onSuccess: onUnassignSettled,
      onError
    });

  // Exactly the delete payload afsprakenplanning sends: the occurrence identifier and the edit scope.
  const runDelete = (editAppointmentType: EditAppointmentTypeResponse) =>
    deleteAppointment({
      appointmentOccurrenceIdentifier: {
        entityId: event.id,
        originalOccurrenceStart: dayjs(event.start).format('YYYY-MM-DD')
      },
      editAppointmentType
    });

  // Remove only this care receiver from the appointment — a CLIENT in `removeParticipants`, the same way
  // the appointments-overview EditParticipants drawer removes a participant. Every other care receiver,
  // employee and resource on the appointment is left untouched.
  const runUnassign = (editAppointmentType: EditAppointmentTypeResponse) =>
    bulkEditParticipants({
      appointmentOccurrenceIdentifiers: [
        {
          entityId: event.id,
          originalOccurrenceStart: event.start,
          schedulingLabel: event.schedulingLabel
        }
      ],
      addParticipants: [],
      removeParticipants: [
        { entityId: careReceiverId, planningEntityType: 'CLIENT' }
      ],
      editAppointmentType
    });

  // A recurring appointment opens the prompt; a single one is carried out at once with ONLY_THIS.
  const startAction = (action: OutsideTreatmentAction) => {
    if (event.isRecurring) {
      setPendingAction(action);
      return;
    }
    if (action === 'delete') {
      runDelete('ONLY_THIS');
    } else {
      runUnassign('ONLY_THIS');
    }
  };

  const confirmRecurrence = (
    editAppointmentType: EditAppointmentTypeResponse
  ) => {
    if (pendingAction === 'delete') {
      runDelete(editAppointmentType);
    } else if (pendingAction === 'unassign') {
      runUnassign(editAppointmentType);
    }
    setPendingAction(null);
  };

  const cancelRecurrence = () => setPendingAction(null);

  return {
    onDelete: () => startAction('delete'),
    onUnassign: () => startAction('unassign'),
    pendingAction,
    confirmRecurrence,
    cancelRecurrence
  };
};
